<template>
  <nav class="pagination" role="navigation" aria-label="Pagination Navigation" data-cy="pagination">
    <div v-if="showCallout()" class="callout" data-testid="noMorePagesCallout">
      <p>
        More 'aha' moments ahead! There are still
        <span class="bold">{{ resourcesRemainingCount }} {{ formatCategoryString }}</span>
        {{ pluralise('resource', resourcesRemainingCount) }} in our library. Narrow down your
        results by using filters or performing a search.
      </p>
    </div>
    <ul class="flex align-middle align-center">
      <li>
        <nuxt-link
          v-if="currentPage === 2"
          :to="{ path: $route.path }"
          @click.native="$emit('click')"
        >
          <span>&larr; Prev</span>
        </nuxt-link>
        <nuxt-link
          v-else-if="currentPage > 2"
          :to="{ path: $route.path, query: { ...$route.query, page: currentPage - 1 } }"
          @click.native="$emit('click')"
        >
          <span>&larr; Prev</span>
        </nuxt-link>
        <span v-else class="disabled">&larr; Prev</span>
      </li>
      <li
        v-for="(page, index) in pageArr"
        v-if="showPageNumbers"
        :key="index"
        :class="{ 'hidden-small': page && !page.current }"
      >
        <span v-if="!page">...</span>
        <strong
          v-else-if="page.current"
          :aria-label="'Current Page, Page ' + page.number"
          aria-current="true"
        >
          <span>{{ page.number }}</span>
        </strong>
        <nuxt-link
          v-else-if="page.number === 1"
          :to="{ path: $route.path }"
          @click.native="$emit('click')"
        >
          <span>{{ page.number }}</span>
        </nuxt-link>
        <nuxt-link
          v-else
          :to="{ path: $route.path, query: { ...$route.query, page: page.number } }"
        >
          <span>{{ page.number }}</span>
        </nuxt-link>
      </li>
      <li>
        <nuxt-link
          v-if="currentPage < pageLimit"
          :to="{ path: $route.path, query: { ...$route.query, page: currentPage + 1 } }"
          @click.native="$emit('click')"
        >
          <span>Next &rarr;</span>
        </nuxt-link>
        <span v-else class="disabled">Next &rarr;</span>
      </li>
    </ul>
  </nav>
</template>

<script>
/* Pagination
 *
 * @parent: /resources/ResourcesList.vue
 * @requests:
 * @events:
 * @props: Number totalPages, Number currentPage, Boolean showPageNumbers
 * @components:
 * @methods:
 */

import { pluralise } from '~/utils/formatStrings';

export default {
  name: 'Pagination',
  props: {
    showPageNumbers: {
      type: Boolean,
      default: false,
      description: 'Show page numbers as well as next and prev button',
    },
    totalPages: { type: Number, default: 1 },
    currentPage: { type: Number, default: 1 },
    size: { type: Number, default: 0 },
    category: { type: String, default: '' },
  },
  data() {
    return {
      pageArr: [],
      maxPages: 30,
      resourcesRemainingCount: 0,
    };
  },
  head() {
    return {
      link: this.paginationLinks,
    };
  },
  computed: {
    paginationLinks() {
      // Construct next and prev meta links for the header
      const url = this.$config.baseURL + this.$route.path;
      const page = parseInt(this.currentPage);
      const links = [];

      // Next link
      if (this.pageLimit > page) {
        links.push({ hid: 'next', rel: 'next', href: url + '?page=' + (page + 1) });
      }

      // Prev links
      if (page === 2 && page <= this.pageLimit) {
        links.push({ hid: 'prev', rel: 'prev', href: url });
      } else if (page > 2 && page <= this.pageLimit) {
        links.push({ hid: 'prev', rel: 'prev', href: url + '?page=' + (page - 1) });
      }

      return links;
    },
    formatCategoryString() {
      const categoryWithoutDashes = this.category.replace(/-/gi, ' ');
      return categoryWithoutDashes[0].toUpperCase() + categoryWithoutDashes.substring(1);
    },
    pageLimit() {
      return Math.min(this.maxPages, this.totalPages);
    },
  },
  watch: {
    currentPage() {
      this.initPages();
    },
    totalPages() {
      this.initPages();
    },
  },
  created() {
    this.resourcesRemainingCount = this.getResourcesRemainingCount();
    this.initPages();
  },
  methods: {
    pluralise,
    initPages() {
      // Construct the pagination number range
      this.pageArr = [];

      let i = 0;
      const range = 2; // Number of pages visible before and after the currentPage

      // Loop through all options
      for (; i < this.pageLimit; i++) {
        if (
          i < 2 ||
          i > this.pageLimit - 3 ||
          (i - range < this.currentPage && i + range * 2 > this.currentPage)
        ) {
          // Add page number to list
          const thisPage = { number: i + 1 };

          if (this.currentPage === thisPage.number) {
            thisPage.current = true;
          }

          this.pageArr.push(thisPage);
        } else if (
          this.pageArr[this.pageArr.length - 1] &&
          this.pageArr[this.pageArr.length - 1].number
        ) {
          // Previous item was a number, then add some dots
          this.pageArr.push(null);
        }
      }
    },
    getResourcesRemainingCount() {
      if (this.totalPages <= this.maxPages) {
        return 0;
      }

      const resourcesToAPage = 32;

      return this.size - this.maxPages * resourcesToAPage;
    },
    showCallout() {
      return this.currentPage >= this.maxPages && this.resourcesRemainingCount && this.category;
    },
  },
};
</script>

<style lang="scss" scoped>
ul {
  justify-content: center;
}

a {
  color: $color-text;
  margin: 0 $base-padding-quarter;
  text-decoration: none;

  &:hover,
  &:focus {
    position: relative;
    z-index: 2;

    > span {
      background-color: $grey-100;
      border-color: $grey-100;
    }
  }

  &:active {
    > span {
      background-color: $grey-200;
      border-color: $grey-200;
    }
  }
}

a,
strong {
  line-height: 1;
}

a > span,
strong > span,
.disabled {
  border: 1px solid $grey-0;
  border-radius: $small-border-radius;
  display: block;
  margin: 0 $base-padding-quarter;
  padding: $base-padding-half $base-padding-two-thirds;
}

.disabled {
  border-color: transparent;
  color: $grey-300;
  cursor: not-allowed;
  line-height: 1;
  margin: 0 $base-padding-quarter;
}

strong {
  display: block;

  > span {
    background-color: $highlighter-500;
    border-color: $highlighter-700;
  }
}

.link {
  background: red;
}
.callout {
  p {
    margin: 0;
  }
}
</style>
